import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { accountingChartsApi, type AccountDto } from '../../services/api';

export type AccountType = 'Asset' | 'Liability' | 'Equity' | 'Revenue' | 'Expense';

export interface Account {
  id: string;
  code: string;
  name: string;
  type: AccountType;
  subtype: string;
  description: string;
  balance: number;
  status: 'Active' | 'Inactive';
}

const ACCOUNT_TYPES: AccountType[] = ['Asset', 'Liability', 'Equity', 'Revenue', 'Expense'];

// Subtype dropdown list (matches Create New Account reference)
const ACCOUNT_SUBTYPES = [
  'Current Asset',
  'Fixed Asset',
  'Current Liability',
  'Long-term Liability',
  'Retained Earnings',
  'Capital',
  'Operating Revenue',
  'Other Revenue',
  'Cost of Sales',
  'Operating Expense',
  'Other Expense',
];

const TYPE_PILL_CLASS: Record<AccountType, string> = {
  Asset: 'bg-blue-100 text-blue-800',
  Liability: 'bg-red-100 text-red-800',
  Equity: 'bg-brand-100 text-brand-700',
  Revenue: 'bg-emerald-100 text-emerald-800',
  Expense: 'bg-amber-100 text-amber-800',
};

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-PH', { style: 'currency', currency: 'PHP' }).format(value);
}

function dtoToAccount(d: AccountDto): Account {
  return {
    id: d.id,
    code: d.code,
    name: d.name,
    type: d.type as AccountType,
    subtype: d.subtype ?? '',
    description: d.description ?? '',
    balance: d.balance,
    status: (d.status === 'Inactive' ? 'Inactive' : 'Active') as 'Active' | 'Inactive',
  };
}

interface CreateAccountModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (account: Omit<Account, 'id' | 'balance' | 'status'>) => Promise<boolean>;
  editAccount: Account | null;
  apiError: string | null;
  setApiError: (s: string | null) => void;
}

function CreateAccountModal({ open, onClose, onSubmit, editAccount, apiError, setApiError }: CreateAccountModalProps) {
  const [code, setCode] = useState('');
  const [type, setType] = useState<AccountType>('Asset');
  const [name, setName] = useState('');
  const [subtype, setSubtype] = useState('');
  const [description, setDescription] = useState('');
  const [typeDropdownOpen, setTypeDropdownOpen] = useState(false);
  const [subtypeDropdownOpen, setSubtypeDropdownOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const typeRef = useRef<HTMLDivElement>(null);
  const subtypeRef = useRef<HTMLDivElement>(null);

  const isEditing = !!editAccount;

  useEffect(() => {
    if (open) setApiError(null);
  }, [open, setApiError]);

  useEffect(() => {
    if (open) {
      if (editAccount) {
        setCode(editAccount.code);
        setType(editAccount.type);
        setName(editAccount.name);
        setSubtype(editAccount.subtype);
        setDescription(editAccount.description);
      } else {
        setCode('');
        setType('Asset');
        setName('');
        setSubtype('');
        setDescription('');
      }
    }
  }, [open, editAccount]);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (typeRef.current && !typeRef.current.contains(e.target as Node)) setTypeDropdownOpen(false);
      if (subtypeRef.current && !subtypeRef.current.contains(e.target as Node)) setSubtypeDropdownOpen(false);
    };
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!code.trim() || !name.trim()) return;
    setSaving(true);
    setApiError(null);
    try {
      const success = await onSubmit({
        code: code.trim(),
        type,
        name: name.trim(),
        subtype: subtype.trim(),
        description: description.trim(),
      });
      if (success) onClose();
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} aria-hidden />
      <div className="relative bg-white rounded-xl shadow-xl border border-gray-200 w-full max-w-md overflow-visible flex flex-col max-h-[85vh]">
        <div className="shrink-0 bg-white border-b border-gray-100 px-5 py-3 flex items-center justify-between rounded-t-xl">
          <h2 className="text-lg font-bold text-gray-900">
            {isEditing ? 'Edit Account' : 'Create New Account'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100"
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-3 flex-1 min-h-0 flex flex-col overflow-visible">
          <div className="shrink-0">
            <label className="block text-sm font-medium text-gray-700 mb-1">Account Code</label>
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="e.g., 1000"
              className="w-full rounded-lg border border-gray-200 bg-gray-50/80 px-3 py-2 text-gray-900 placeholder-gray-400 focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 focus:bg-white outline-none text-sm"
              required
            />
          </div>
          <div className="shrink-0 relative" ref={typeRef}>
            <label className="block text-sm font-medium text-gray-700 mb-1">Account Type</label>
            <button
              type="button"
              onClick={() => setTypeDropdownOpen(!typeDropdownOpen)}
              className="w-full rounded-lg border border-gray-200 bg-gray-50/80 px-3 py-2 text-left text-sm text-gray-900 flex items-center justify-between"
            >
              <span>{type}</span>
              <svg
                className={`w-4 h-4 text-gray-400 transition-transform ${typeDropdownOpen ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {typeDropdownOpen && (
              <div className="absolute z-50 mt-1 left-0 right-0 rounded-lg border border-gray-200 bg-white py-1 shadow-lg">
                {ACCOUNT_TYPES.map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => {
                      setType(t);
                      setTypeDropdownOpen(false);
                    }}
                    className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center justify-between"
                  >
                    {t}
                    {type === t && (
                      <svg className="w-4 h-4 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="shrink-0">
            <label className="block text-sm font-medium text-gray-700 mb-1">Account Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Cash on Hand"
              className="w-full rounded-lg border border-gray-200 bg-gray-50/80 px-3 py-2 text-gray-900 placeholder-gray-400 focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 focus:bg-white outline-none text-sm"
              required
            />
          </div>
          <div className="shrink-0 relative" ref={subtypeRef}>
            <label className="block text-sm font-medium text-gray-700 mb-1">Subtype</label>
            <button
              type="button"
              onClick={() => setSubtypeDropdownOpen(!subtypeDropdownOpen)}
              className="w-full rounded-lg border border-gray-200 bg-gray-50/80 px-3 py-2 text-left text-sm flex items-center justify-between"
            >
              <span className={subtype ? 'text-gray-900' : 'text-gray-400'}>{subtype || 'Select subtype'}</span>
              <svg
                className={`w-4 h-4 text-gray-400 transition-transform ${subtypeDropdownOpen ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {subtypeDropdownOpen && (
              <div className="absolute z-50 bottom-full left-0 right-0 mb-1 rounded-lg border border-gray-200 bg-white py-2 px-2 shadow-lg grid grid-cols-2 gap-0.5">
                {ACCOUNT_SUBTYPES.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => {
                      setSubtype(s);
                      setSubtypeDropdownOpen(false);
                    }}
                    className="px-3 py-2 rounded-md text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center justify-between gap-2"
                  >
                    <span className="truncate">{s}</span>
                    {subtype === s && (
                      <svg className="w-4 h-4 shrink-0 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="shrink-0">
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional description"
              className="w-full rounded-lg border border-gray-200 bg-gray-50/80 px-3 py-2 text-gray-900 placeholder-gray-400 focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 focus:bg-white outline-none text-sm"
            />
          </div>
          {apiError && (
            <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
              {apiError}
            </div>
          )}
          <div className="flex justify-end gap-2 pt-3 shrink-0">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="px-4 py-2.5 rounded-lg text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2.5 rounded-lg text-sm font-medium text-white bg-brand-600 hover:bg-brand-700 disabled:opacity-50"
            >
              {saving ? 'Saving...' : isEditing ? 'Save Changes' : 'Create Account'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function ChartOfAccountsPage() {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editAccount, setEditAccount] = useState<Account | null>(null);
  const [actionMenuId, setActionMenuId] = useState<string | null>(null);
  const actionMenuRef = useRef<HTMLTableCellElement>(null);

  const fetchAccounts = useCallback(async () => {
    setLoading(true);
    setApiError(null);
    try {
      const res = await accountingChartsApi.list();
      if (res.data) setAccounts(res.data.map(dtoToAccount));
      else setAccounts([]);
    } catch {
      setApiError('Failed to load accounts.');
      setAccounts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  const filteredAccounts = useMemo(() => {
    if (!search.trim()) return accounts;
    const q = search.trim().toLowerCase();
    return accounts.filter(
      (a) =>
        a.code.toLowerCase().includes(q) ||
        a.name.toLowerCase().includes(q) ||
        a.type.toLowerCase().includes(q)
    );
  }, [accounts, search]);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (actionMenuRef.current && !actionMenuRef.current.contains(e.target as Node)) setActionMenuId(null);
    };
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  const handleCreateAccount = async (data: Omit<Account, 'id' | 'balance' | 'status'>): Promise<boolean> => {
    setApiError(null);
    if (editAccount) {
      const res = await accountingChartsApi.update(editAccount.id, data);
      if (!res.data) {
        setApiError(res.message || 'Failed to update account.');
        return false;
      }
      setAccounts((prev) =>
        prev.map((a) => (a.id === editAccount.id ? dtoToAccount(res.data!) : a))
      );
      setEditAccount(null);
      return true;
    }
    const res = await accountingChartsApi.create(data);
    if (!res.data) {
      setApiError(res.message || 'Failed to create account.');
      return false;
    }
    setAccounts((prev) => [...prev, dtoToAccount(res.data!)]);
    return true;
  };

  const openEdit = (account: Account) => {
    setEditAccount(account);
    setModalOpen(true);
    setActionMenuId(null);
  };

  const handleDelete = async (id: string) => {
    setApiError(null);
    const res = await accountingChartsApi.delete(id);
    if (!res.success) {
      setApiError(res.message || 'Failed to delete account.');
      return;
    }
    setAccounts((prev) => prev.filter((a) => a.id !== id));
    setActionMenuId(null);
  };

  return (
    <div className="max-w-7xl mx-auto w-full flex-1 min-h-0 flex flex-col overflow-hidden">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div>
          <h1 className="text-lg font-bold text-gray-900">Chart of Accounts</h1>
          <p className="text-sm text-gray-500 mt-0.5">Manage your general ledger accounts</p>
        </div>
        <button
          type="button"
          onClick={() => {
            setEditAccount(null);
            setModalOpen(true);
          }}
          className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium text-white bg-brand-600 hover:bg-brand-700 shrink-0"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add Account
        </button>
      </div>

      <div className="relative mb-4">
        <svg
          className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search accounts..."
          className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-200 bg-gray-50 focus:bg-white focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 outline-none text-sm"
        />
      </div>

      {apiError && !modalOpen && (
        <div className="mb-4 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
          {apiError}
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-200 shadow-card flex-1 min-h-0 flex flex-col overflow-hidden">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-16 text-gray-500">
            <svg
              className="w-10 h-10 animate-spin text-brand-500 mb-3"
              fill="none"
              viewBox="0 0 24 24"
              aria-hidden
            >
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            <p className="font-medium text-gray-600">Loading accounts...</p>
          </div>
        ) : filteredAccounts.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center text-gray-500 flex-1">
            <span className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center text-gray-400 mb-4">
              <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                />
              </svg>
            </span>
            <p className="font-medium text-gray-600">No accounts yet</p>
            <p className="text-sm mt-1 max-w-sm">
              {accounts.length === 0
                ? 'Get started by adding your first account to the chart of accounts.'
                : 'No accounts match your search.'}
            </p>
            {accounts.length === 0 && (
              <button
                type="button"
                onClick={() => setModalOpen(true)}
                className="mt-4 inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium text-white bg-brand-600 hover:bg-brand-700"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add Account
              </button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto flex-1 min-h-0">
            <table className="w-full min-w-[640px]">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50/80">
                  <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">
                    Code
                  </th>
                  <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">
                    Account Name
                  </th>
                  <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">
                    Type
                  </th>
                  <th
                    className="text-right text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3"
                    title="Balance is computed from journal entry lines (debit − credit) for this account."
                  >
                    Balance
                  </th>
                  <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">
                    Status
                  </th>
                  <th className="w-12 px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredAccounts.map((account) => (
                  <tr key={account.id} className="hover:bg-gray-50/50">
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{account.code}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{account.name}</td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${TYPE_PILL_CLASS[account.type]}`}
                      >
                        {account.type}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-right tabular-nums text-gray-700">
                      {formatCurrency(account.balance)}
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
                        {account.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 relative" ref={actionMenuId === account.id ? actionMenuRef : undefined}>
                      <button
                        type="button"
                        onClick={() => setActionMenuId(actionMenuId === account.id ? null : account.id)}
                        className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                        aria-label="Actions"
                      >
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
                        </svg>
                      </button>
                      {actionMenuId === account.id && (
                        <div className="absolute right-4 top-full mt-1 w-36 rounded-lg border border-gray-200 bg-white py-1 shadow-lg z-10">
                          <button
                            type="button"
                            onClick={() => openEdit(account)}
                            className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDelete(account.id)}
                            className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50"
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <CreateAccountModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setEditAccount(null);
          setApiError(null);
        }}
        onSubmit={handleCreateAccount}
        editAccount={editAccount}
        apiError={apiError}
        setApiError={setApiError}
      />
    </div>
  );
}
